import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import CriteriaTable from "../../components/CriteriaTable/CriteriaTable";
// import { clubCriteria } from "../../assets/staticData";
import { Box } from "@mui/material";
import DetailedCriteria from "../DetailedCriteria";
import {
  getSelfAssessmentCriteria,
  getOffSiteCriteria,
  getCriteriaGroups,
  getOnSiteCriteria,
  getClubReviewsInfo,
  getEvaluationReportCriteria,
  restrictCriteriaUsers,
} from "../../api/evaluation";
import { getClubsDesirePlan } from "../../api/club";
import { setClubDesirePlan } from "../../redux/reducers/clubData";
import {
  setCriteriaGroups,
  setEvalReportCriteria,
  setOffSiteCriteria,
  setOnSiteCriteria,
  setSaCriteria,
  setSelectedOnSiteSession,
} from "../../redux/reducers/evaluation";

import EvalReportCriteriaTable from "../../components/CriteriaTable/EvalReportCriteriaTable";
import ContentOverview from "../../components/ContentOverview";
import { sortData } from "../../common/SortSupport";
import OnSiteWrapper from "../OnSiteWrapper";
import Calendar from "../../components/Calendar";
import { useTranslation } from "react-i18next";
import { formatDate, onsiteSelectedCriteriaRegex, onsiteSelectedSessionRegex, onsiteSessionCriteriaRegex } from "../../common/StaticExports";
import { useLocation } from "react-router";

const ClubCriteria = () => {
  const location = useLocation();
  const isAdmin = useSelector((state:any) => state.common.isAdmin);
  const clubDetails = useSelector((state: any) => state.clubDetails);
  const selectedCriteria = useSelector(
    (state: any) => state.evaluation.selectedSaCriteria
  );
  const criterias = useSelector((state: any) => state.evaluation.saCriteria);
  const searchString = useSelector(
    (state: any) => state.evaluation.searchString
  );
  const currentSection = useSelector(
    (state: any) => state.evaluation.currentEvaluationTab
  );
  const evalReportCriteria = useSelector(
    (state: any) => state.evaluation.evalReportCriteria
  );
  const selectedEvalReportCriteria = useSelector(
    (state: any) => state.evaluation.selectedEvalReportCriteria
  );
  const criteriaGroups = useSelector(
    (state: any) => state.evaluation.criteriaGroups
  );
  const interviewView = useSelector(
    (state: any) => state.evaluation.interviewView
  );
  const onSiteCriteria = useSelector(
    (state: any) => state.evaluation.onSiteCriteria
  );
  const dispatch = useDispatch();
  const [isFetching, setIsFetching] = useState(false);
  const [isFetchingOnSite, setIsFetchingOnSite] = useState(false);
  const auth: any = localStorage.getItem("_jauth_");
  const parsedAuth = auth ? JSON.parse(auth) : null;
  const idClub = clubDetails?.id || parsedAuth.clubId;
  const [t] = useTranslation();
  
  // Use local state for filtered criteria
  const [filteredCriteria, setFilteredCriteria] = useState<any[]>([]);

  useEffect(() => {
    const fetchDesirePlan = async () => {
      try {
        const response = await getClubsDesirePlan("desirePlan", {
          clubId: idClub,
        });
        return response;
      } catch (error) {
        console.error("Error while fetching getClubsDesirePlan");
      }
    };
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    idClub &&
      fetchDesirePlan()?.then((res: any) => {
        dispatch(setClubDesirePlan(res));
      });
  }, [clubDetails, dispatch]);

  //fetch SA Criteria
  const fetchCriteria = useCallback(async () => {
    const response = await getSelfAssessmentCriteria({ clubId: idClub });
    const groups = await getCriteriaGroups({ clubId: idClub });
    var tempArr = response;
    if (tempArr?.length > 0 && response?.length > 0) {
      tempArr = sortData(response, "uid", "asc");
    }
    // Store in Redux - this persists across navigation
    dispatch(setSaCriteria(tempArr || response));
    dispatch(setCriteriaGroups(groups));
    setIsFetching(false);
    return response;
  }, [idClub, dispatch]);

  //fetch Off-Site Criteria
  const fetchOffSiteCriteria = useCallback(async () => {
    const response = await getOffSiteCriteria({ clubId: idClub });
    dispatch(setOffSiteCriteria(response));
  }, [idClub, dispatch]);

  //fetch On-Site Criteria
  const fetchOnSiteSessions = useCallback(async () => {
    const reviewData = await getClubReviewsInfo({ clubId: idClub });
    const response = await getOnSiteCriteria({
      onSiteReviewId: reviewData.onSiteReviewId,
    });
    dispatch(setOnSiteCriteria(response));
    return response;
  }, [idClub, dispatch]);

  //fetch evaluation report criteria
  const fetchEvalCriteria = useCallback(async () => {
    const response = await getEvaluationReportCriteria({ clubId: idClub });
    dispatch(setEvalReportCriteria(response));
  }, [idClub, dispatch]);

  useEffect(() => {
    if (idClub) {
      switch (currentSection) {
        case t("EVALUATION"):
          if (!criterias && !isFetching) {
            setIsFetching(true);
            fetchCriteria();
          } 
          if ((!onSiteCriteria && !isFetchingOnSite) && isAdmin) {
            setIsFetchingOnSite(true);
            fetchOnSiteSessions().then((res:any) => {setIsFetchingOnSite(false);});
          }
          break;
        case t("OFF_SITE"):
          if (!criterias && !isFetching) {
            fetchOffSiteCriteria();
          }
          break;
        case t("ON_SITE"):
          if ((!onSiteCriteria && !isFetchingOnSite) && isAdmin) {
            setIsFetchingOnSite(true);
            fetchOnSiteSessions().then((res:any) => {setIsFetchingOnSite(false);});
          }
          if (!criterias && !isFetching) {
            fetchCriteria();
          }
          break;
        case t("EVALUATION_REPORT"):
          fetchEvalCriteria();
          break;
      }
    }
  }, [idClub, currentSection, criterias, isFetching, onSiteCriteria, isFetchingOnSite, isAdmin, t, fetchCriteria, fetchOffSiteCriteria, fetchOnSiteSessions, fetchEvalCriteria]);

  // Filter criteria based on search string using useState
  useEffect(() => {
    if (!criterias || criterias.length === 0) {
      setFilteredCriteria([]);
      return;
    }
    
    if (!searchString || searchString.trim() === '') {
      setFilteredCriteria(criterias);
      return;
    }
    
    const filtered = criterias.filter(
      (criteria: any) =>
        criteria.name.toLowerCase().includes(searchString.toLowerCase()) ||
        criteria.uid.toLowerCase().includes(searchString.toLowerCase())
    );
    
    setFilteredCriteria(filtered);
  }, [criterias, searchString]);

  
  useEffect(() => {
    if (onsiteSelectedSessionRegex.test(location.pathname) || onsiteSessionCriteriaRegex.test(location.pathname)) {
      const tempSessionId = location.pathname.split('/')[3];
      if (!onSiteCriteria && !isFetchingOnSite) {
        setIsFetchingOnSite(true);
        fetchOnSiteSessions().then((res:any) => {
        const tempSelectedSession = res?.find((d:any) => d?.id == tempSessionId);
        dispatch(setSelectedOnSiteSession(tempSelectedSession));
        setIsFetchingOnSite(false);
        });
      } else if (onSiteCriteria) {
        const tempSelectedSession = onSiteCriteria?.find((d:any) => d?.id == tempSessionId);
        dispatch(setSelectedOnSiteSession(tempSelectedSession));
      }
    }
  }, [location.pathname, onSiteCriteria, isFetchingOnSite, dispatch, fetchOnSiteSessions]);

  return (
    <React.Fragment>
      <Box sx={{ height: "100%" }}>
        {
          currentSection == t("EVALUATION") &&
          selectedCriteria && 
          Object.keys(selectedCriteria)?.length > 0 &&
          <ContentOverview
            data={criteriaGroups?.find(
              (x: any) => x.id == selectedCriteria?.criteriaGroupId
            )}
            from={"SA"}
            criterias={filteredCriteria?.filter(
              (x: any) => x.criteriaGroupId == selectedCriteria?.criteriaGroupId
            )}
          />
        }
        {
          currentSection == t("EVALUATION") &&
          (
            <Box
              sx={{
                height: "100%",
                overflowY: selectedCriteria && Object.keys(selectedCriteria)?.length
                  ? "hidden"
                  : "auto",
              }}
            >
              {
                !isFetching &&
                filteredCriteria &&
                filteredCriteria.length > 0 &&
                criteriaGroups &&
                criteriaGroups.length > 0 ?
                criteriaGroups?.map((group: any, i: any) => {
                  var filteredData = filteredCriteria?.filter(
                    (x: any) => x.criteriaGroupId == group.id
                  );
                  var calcHeight = () => {
                    return "fit-content";
                  };
                  
                  return (
                    <>
                        {
                          (!selectedCriteria || Object.keys(selectedCriteria)?.length === 0) &&
                          <ContentOverview
                            data={group}
                            from={"SA"}
                            criterias={filteredData}
                          />
                        }
                        <Box
                          sx={
                            selectedCriteria && Object.keys(selectedCriteria)?.length
                              ? {
                                  display: i < 1 ? "flex" : "none",
                                  mt: 1,
                                  height: "calc(100% - 27px)",
                                  mb: "20px",
                                }
                              : { mt: 1, height: calcHeight(), mb: "20px" }
                          }
                        >
                          <CriteriaTable
                            data={
                              selectedCriteria && Object.keys(selectedCriteria)?.length > 0
                                ? filteredCriteria
                                : filteredData
                            }
                            criteria={!selectedCriteria || Object.keys(selectedCriteria)?.length === 0}
                            selectedCriteria={selectedCriteria}
                            isLoading={isFetching}
                            triggerFetchCriteria={fetchCriteria}
                          />
                          {selectedCriteria && Object.keys(selectedCriteria)?.length > 0 && i < 1 && (
                            <DetailedCriteria
                              criteria={filteredCriteria.find((item: any) => {
                                return selectedCriteria?.id == item?.id;
                              })}
                              module={t("EVALUATION")}
                            />
                          )}
                        </Box>
                    </>
                  )
                }): (
                <Box sx={{ mt: 1, height: "calc(100% - 27px)", mb: "20px" }}>
                  <CriteriaTable
                    data={[]}
                    criteria={!selectedCriteria || Object.keys(selectedCriteria)?.length === 0}
                    selectedCriteria={selectedCriteria}
                    isLoading={isFetching}
                    triggerFetchCriteria={fetchCriteria}
                  />
                </Box> )
              }
            </Box>
          )
        }
        {currentSection == t("ON_SITE") &&
          (interviewView == "CALENDAR" ? (
            <Box sx={{ height: "90%", width: "100%" }}>
              <Calendar
                data={
                  onSiteCriteria
                    ? onSiteCriteria?.map((d: any) => {
                        return {
                          title: d.name,
                          allDay: true,
                          date: formatDate(d?.date),
                        };
                      })
                    : []
                }
              />
            </Box>
          ) : (
            <OnSiteWrapper isLoading={isFetchingOnSite} />
          ))}
        {currentSection == t("EVALUATION_REPORT") && (
          <Box
            sx={
              selectedCriteria && Object.keys(selectedEvalReportCriteria)?.length
                ? { display: "flex", mt: 1, height: "100%" }
                : { mt: 1, height: "100%" }
            }
          >
            <EvalReportCriteriaTable
              data={evalReportCriteria}
              criteria={selectedCriteria && Object.keys(selectedEvalReportCriteria)?.length === 0}
              selectedCriteria={selectedEvalReportCriteria}
            />
            {selectedCriteria && Object.keys(selectedEvalReportCriteria)?.length > 0 && (
              <DetailedCriteria
                criteria={selectedEvalReportCriteria}
                module={t("EVALUATION")}
              />
            )}
          </Box>
        )}
      </Box>
    </React.Fragment>
  );
};

export default ClubCriteria;